// Copyright (c) .NET Foundation. All rights reserved.
// Licensed under the Apache License, Version 2.0. See License.txt in the project root for license information.
var __awaiter = this && this.__awaiter || function (thisArg, _arguments, P, generator) {
  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }

    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }

    function step(result) {
      result.done ? resolve(result.value) : new P(function (resolve) {
        resolve(result.value);
      }).then(fulfilled, rejected);
    }

    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
};

var __generator = this && this.__generator || function (thisArg, body) {
  var _ = {
    label: 0,
    sent: function sent() {
      if (t[0] & 1) throw t[1];
      return t[1];
    },
    trys: [],
    ops: []
  },
      f,
      y,
      t,
      g;
  return g = {
    next: verb(0),
    "throw": verb(1),
    "return": verb(2)
  }, typeof Symbol === "function" && (g[Symbol.iterator] = function () {
    return this;
  }), g;

  function verb(n) {
    return function (v) {
      return step([n, v]);
    };
  }

  function step(op) {
    if (f) throw new TypeError("Generator is already executing.");

    while (_) {
      try {
        if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
        if (y = 0, t) op = [op[0] & 2, t.value];

        switch (op[0]) {
          case 0:
          case 1:
            t = op;
            break;

          case 4:
            _.label++;
            return {
              value: op[1],
              done: false
            };

          case 5:
            _.label++;
            y = op[1];
            op = [0];
            continue;

          case 7:
            op = _.ops.pop();

            _.trys.pop();

            continue;

          default:
            if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
              _ = 0;
              continue;
            }

            if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
              _.label = op[1];
              break;
            }

            if (op[0] === 6 && _.label < t[1]) {
              _.label = t[1];
              t = op;
              break;
            }

            if (t && _.label < t[2]) {
              _.label = t[2];

              _.ops.push(op);

              break;
            }

            if (t[2]) _.ops.pop();

            _.trys.pop();

            continue;
        }

        op = body.call(thisArg, _);
      } catch (e) {
        op = [6, e];
        y = 0;
      } finally {
        f = t = 0;
      }
    }

    if (op[0] & 5) throw op[1];
    return {
      value: op[0] ? op[1] : void 0,
      done: true
    };
  }
};

import { HandshakeProtocol } from "./HandshakeProtocol";
import { MessageType } from "./IHubProtocol";
import { LogLevel } from "./ILogger";
import { Arg, Subject } from "./Utils";
var DEFAULT_TIMEOUT_IN_MS = 30 * 1000;
var DEFAULT_PING_INTERVAL_IN_MS = 15 * 1000;
/** Describes the current state of the {@link HubConnection} to the server. */

export var HubConnectionState;

(function (HubConnectionState) {
  /** The hub connection is disconnected. */
  HubConnectionState[HubConnectionState["Disconnected"] = 0] = "Disconnected";
  /** The hub connection is connected. */

  HubConnectionState[HubConnectionState["Connected"] = 1] = "Connected";
})(HubConnectionState || (HubConnectionState = {}));
/** Represents a connection to a SignalR Hub. */


var HubConnection =
/** @class */
function () {
  function HubConnection(connection, logger, protocol) {
    var _this = this;

    Arg.isRequired(connection, "connection");
    Arg.isRequired(logger, "logger");
    Arg.isRequired(protocol, "protocol");
    this.serverTimeoutInMilliseconds = DEFAULT_TIMEOUT_IN_MS;
    this.keepAliveIntervalInMilliseconds = DEFAULT_PING_INTERVAL_IN_MS;
    this.logger = logger;
    this.protocol = protocol;
    this.connection = connection;
    this.handshakeProtocol = new HandshakeProtocol();

    this.connection.onreceive = function (data) {
      return _this.processIncomingData(data);
    };

    this.connection.onclose = function (error) {
      return _this.connectionClosed(error);
    };

    this.callbacks = {};
    this.methods = {};
    this.closedCallbacks = [];
    this.id = 0;
    this.receivedHandshakeResponse = false;
    this.connectionState = HubConnectionState.Disconnected;
    this.cachedPingMessage = this.protocol.writeMessage({
      type: MessageType.Ping
    });
  }
  /** @internal */
  // Using a public static factory method means we can have a private constructor and an _internal_
  // create method that can be used by HubConnectionBuilder. An "internal" constructor would just
  // be stripped away and the '.d.ts' file would have no constructor, which is interpreted as a
  // public parameter-less constructor.


  HubConnection.create = function (connection, logger, protocol) {
    return new HubConnection(connection, logger, protocol);
  };

  Object.defineProperty(HubConnection.prototype, "state", {
    /** Indicates the state of the {@link HubConnection} to the server. */
    get: function get() {
      return this.connectionState;
    },
    enumerable: true,
    configurable: true
  });
  /** Starts the connection.
   *
   * @returns {Promise<void>} A Promise that resolves when the connection has been successfully established, or rejects with an error.
   */

  HubConnection.prototype.start = function () {
    return __awaiter(this, void 0, void 0, function () {
      var handshakeRequest, handshakePromise;

      var _this = this;

      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            handshakeRequest = {
              protocol: this.protocol.name,
              version: this.protocol.version
            };
            this.logger.log(LogLevel.Debug, "Starting HubConnection.");
            this.receivedHandshakeResponse = false;
            handshakePromise = new Promise(function (resolve, reject) {
              _this.handshakeResolver = resolve;
              _this.handshakeRejecter = reject;
            });
            return [4
            /*yield*/
            , this.connection.start(this.protocol.transferFormat)];

          case 1:
            _a.sent();

            this.logger.log(LogLevel.Debug, "Sending handshake request.");
            return [4
            /*yield*/
            , this.sendMessage(this.handshakeProtocol.writeHandshakeRequest(handshakeRequest))];

          case 2:
            _a.sent();

            this.logger.log(LogLevel.Information, "Using HubProtocol '" + this.protocol.name + "'."); // defensively cleanup timeout in case we receive a message from the server before we finish start

            this.cleanupTimeout();
            this.resetTimeoutPeriod();
            this.resetKeepAliveInterval(); // Wait for the handshake to complete before marking connection as connected

            return [4
            /*yield*/
            , handshakePromise];

          case 3:
            // Wait for the handshake to complete before marking connection as connected
            _a.sent();

            this.connectionState = HubConnectionState.Connected;
            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /** Stops the connection.
   *
   * @returns {Promise<void>} A Promise that resolves when the connection has been successfully terminated, or rejects with an error.
   */


  HubConnection.prototype.stop = function () {
    this.logger.log(LogLevel.Debug, "Stopping HubConnection.");
    this.cleanupTimeout();
    this.cleanupPingTimer();
    return this.connection.stop();
  };
  /** Invokes a streaming hub method on the server using the specified name and arguments.
   *
   * @typeparam T The type of the items returned by the server.
   * @param {string} methodName The name of the server method to invoke.
   * @param {any[]} args The arguments used to invoke the server method.
   * @returns {IStreamResult<T>} An object that yields results from the server as they are received.
   */


  HubConnection.prototype.stream = function (methodName) {
    var _this = this;

    var args = [];

    for (var _i = 1; _i < arguments.length; _i++) {
      args[_i - 1] = arguments[_i];
    }

    var invocationDescriptor = this.createStreamInvocation(methodName, args);
    var promiseQueue;
    var subject = new Subject();

    subject.cancelCallback = function () {
      var cancelInvocation = _this.createCancelInvocation(invocationDescriptor.invocationId);

      var cancelMessage = _this.protocol.writeMessage(cancelInvocation);

      delete _this.callbacks[invocationDescriptor.invocationId];
      return promiseQueue.then(function () {
        return _this.sendMessage(cancelMessage);
      });
    };

    this.callbacks[invocationDescriptor.invocationId] = function (invocationEvent, error) {
      if (error) {
        subject.error(error);
        return;
      } else if (invocationEvent) {
        // invocationEvent will not be null when an error is not passed to the callback
        if (invocationEvent.type === MessageType.Completion) {
          if (invocationEvent.error) {
            subject.error(new Error(invocationEvent.error));
          } else {
            subject.complete();
          }
        } else {
          subject.next(invocationEvent.item);
        }
      }
    };

    var message = this.protocol.writeMessage(invocationDescriptor);
    promiseQueue = this.sendMessage(message).catch(function (e) {
      subject.error(e);
      delete _this.callbacks[invocationDescriptor.invocationId];
    });
    return subject;
  };

  HubConnection.prototype.sendMessage = function (message) {
    this.resetKeepAliveInterval();
    return this.connection.send(message);
  };
  /** Invokes a hub method on the server using the specified name and arguments. Does not wait for a response from the receiver.
   *
   * The Promise returned by this method resolves when the client has sent the invocation to the server. The server may still
   * be processing the invocation.
   *
   * @param {string} methodName The name of the server method to invoke.
   * @param {any[]} args The arguments used to invoke the server method.
   * @returns {Promise<void>} A Promise that resolves when the invocation has been successfully sent, or rejects with an error.
   */


  HubConnection.prototype.send = function (methodName) {
    var args = [];

    for (var _i = 1; _i < arguments.length; _i++) {
      args[_i - 1] = arguments[_i];
    }

    var invocationDescriptor = this.createInvocation(methodName, args, true);
    var message = this.protocol.writeMessage(invocationDescriptor);
    return this.sendMessage(message);
  };
  /** Invokes a hub method on the server using the specified name and arguments.
   *
   * The Promise returned by this method resolves when the server indicates it has finished invoking the method. When the promise
   * resolves, the server has finished invoking the method. If the server method returns a result, it is produced as the result of
   * resolving the Promise.
   *
   * @typeparam T The expected return type.
   * @param {string} methodName The name of the server method to invoke.
   * @param {any[]} args The arguments used to invoke the server method.
   * @returns {Promise<T>} A Promise that resolves with the result of the server method (if any), or rejects with an error.
   */


  HubConnection.prototype.invoke = function (methodName) {
    var _this = this;

    var args = [];

    for (var _i = 1; _i < arguments.length; _i++) {
      args[_i - 1] = arguments[_i];
    }

    var invocationDescriptor = this.createInvocation(methodName, args, false);
    var p = new Promise(function (resolve, reject) {
      // invocationId will always have a value for a non-blocking invocation
      _this.callbacks[invocationDescriptor.invocationId] = function (invocationEvent, error) {
        if (error) {
          reject(error);
          return;
        } else if (invocationEvent) {
          // invocationEvent will not be null when an error is not passed to the callback
          if (invocationEvent.type === MessageType.Completion) {
            if (invocationEvent.error) {
              reject(new Error(invocationEvent.error));
            } else {
              resolve(invocationEvent.result);
            }
          } else {
            reject(new Error("Unexpected message type: " + invocationEvent.type));
          }
        }
      };

      var message = _this.protocol.writeMessage(invocationDescriptor);

      _this.sendMessage(message).catch(function (e) {
        reject(e); // invocationId will always have a value for a non-blocking invocation

        delete _this.callbacks[invocationDescriptor.invocationId];
      });
    });
    return p;
  };
  /** Registers a handler that will be invoked when the hub method with the specified method name is invoked.
   *
   * @param {string} methodName The name of the hub method to define.
   * @param {Function} newMethod The handler that will be raised when the hub method is invoked.
   */


  HubConnection.prototype.on = function (methodName, newMethod) {
    if (!methodName || !newMethod) {
      return;
    }

    methodName = methodName.toLowerCase();

    if (!this.methods[methodName]) {
      this.methods[methodName] = [];
    } // Preventing adding the same handler multiple times.


    if (this.methods[methodName].indexOf(newMethod) !== -1) {
      return;
    }

    this.methods[methodName].push(newMethod);
  };

  HubConnection.prototype.off = function (methodName, method) {
    if (!methodName) {
      return;
    }

    methodName = methodName.toLowerCase();
    var handlers = this.methods[methodName];

    if (!handlers) {
      return;
    }

    if (method) {
      var removeIdx = handlers.indexOf(method);

      if (removeIdx !== -1) {
        handlers.splice(removeIdx, 1);

        if (handlers.length === 0) {
          delete this.methods[methodName];
        }
      }
    } else {
      delete this.methods[methodName];
    }
  };
  /** Registers a handler that will be invoked when the connection is closed.
   *
   * @param {Function} callback The handler that will be invoked when the connection is closed. Optionally receives a single argument containing the error that caused the connection to close (if any).
   */


  HubConnection.prototype.onclose = function (callback) {
    if (callback) {
      this.closedCallbacks.push(callback);
    }
  };

  HubConnection.prototype.processIncomingData = function (data) {
    this.cleanupTimeout();

    if (!this.receivedHandshakeResponse) {
      data = this.processHandshakeResponse(data);
      this.receivedHandshakeResponse = true;
    } // Data may have all been read when processing handshake response


    if (data) {
      // Parse the messages
      var messages = this.protocol.parseMessages(data, this.logger);

      for (var _i = 0, messages_1 = messages; _i < messages_1.length; _i++) {
        var message = messages_1[_i];

        switch (message.type) {
          case MessageType.Invocation:
            this.invokeClientMethod(message);
            break;

          case MessageType.StreamItem:
          case MessageType.Completion:
            var callback = this.callbacks[message.invocationId];

            if (callback != null) {
              if (message.type === MessageType.Completion) {
                delete this.callbacks[message.invocationId];
              }

              callback(message);
            }

            break;

          case MessageType.Ping:
            // Don't care about pings
            break;

          case MessageType.Close:
            this.logger.log(LogLevel.Information, "Close message received from server."); // We don't want to wait on the stop itself.
            // tslint:disable-next-line:no-floating-promises

            this.connection.stop(message.error ? new Error("Server returned an error on close: " + message.error) : undefined);
            break;

          default:
            this.logger.log(LogLevel.Warning, "Invalid message type: " + message.type + ".");
            break;
        }
      }
    }

    this.resetTimeoutPeriod();
  };

  HubConnection.prototype.processHandshakeResponse = function (data) {
    var _a;

    var responseMessage;
    var remainingData;

    try {
      _a = this.handshakeProtocol.parseHandshakeResponse(data), remainingData = _a[0], responseMessage = _a[1];
    } catch (e) {
      var message = "Error parsing handshake response: " + e;
      this.logger.log(LogLevel.Error, message);
      var error = new Error(message); // We don't want to wait on the stop itself.
      // tslint:disable-next-line:no-floating-promises

      this.connection.stop(error);
      this.handshakeRejecter(error);
      throw error;
    }

    if (responseMessage.error) {
      var message = "Server returned handshake error: " + responseMessage.error;
      this.logger.log(LogLevel.Error, message);
      this.handshakeRejecter(message); // We don't want to wait on the stop itself.
      // tslint:disable-next-line:no-floating-promises

      this.connection.stop(new Error(message));
      throw new Error(message);
    } else {
      this.logger.log(LogLevel.Debug, "Server handshake complete.");
    }

    this.handshakeResolver();
    return remainingData;
  };

  HubConnection.prototype.resetKeepAliveInterval = function () {
    var _this = this;

    this.cleanupPingTimer();
    this.pingServerHandle = setTimeout(function () {
      return __awaiter(_this, void 0, void 0, function () {
        var _a;

        return __generator(this, function (_b) {
          switch (_b.label) {
            case 0:
              if (!(this.connectionState === HubConnectionState.Connected)) return [3
              /*break*/
              , 4];
              _b.label = 1;

            case 1:
              _b.trys.push([1, 3,, 4]);

              return [4
              /*yield*/
              , this.sendMessage(this.cachedPingMessage)];

            case 2:
              _b.sent();

              return [3
              /*break*/
              , 4];

            case 3:
              _a = _b.sent(); // We don't care about the error. It should be seen elsewhere in the client.
              // The connection is probably in a bad or closed state now, cleanup the timer so it stops triggering

              this.cleanupPingTimer();
              return [3
              /*break*/
              , 4];

            case 4:
              return [2
              /*return*/
              ];
          }
        });
      });
    }, this.keepAliveIntervalInMilliseconds);
  };

  HubConnection.prototype.resetTimeoutPeriod = function () {
    var _this = this;

    if (!this.connection.features || !this.connection.features.inherentKeepAlive) {
      // Set the timeout timer
      this.timeoutHandle = setTimeout(function () {
        return _this.serverTimeout();
      }, this.serverTimeoutInMilliseconds);
    }
  };

  HubConnection.prototype.serverTimeout = function () {
    // The server hasn't talked to us in a while. It doesn't like us anymore ... :(
    // Terminate the connection, but we don't need to wait on the promise.
    // tslint:disable-next-line:no-floating-promises
    this.connection.stop(new Error("Server timeout elapsed without receiving a message from the server."));
  };

  HubConnection.prototype.invokeClientMethod = function (invocationMessage) {
    var _this = this;

    var methods = this.methods[invocationMessage.target.toLowerCase()];

    if (methods) {
      methods.forEach(function (m) {
        return m.apply(_this, invocationMessage.arguments);
      });

      if (invocationMessage.invocationId) {
        // This is not supported in v1. So we return an error to avoid blocking the server waiting for the response.
        var message = "Server requested a response, which is not supported in this version of the client.";
        this.logger.log(LogLevel.Error, message); // We don't need to wait on this Promise.
        // tslint:disable-next-line:no-floating-promises

        this.connection.stop(new Error(message));
      }
    } else {
      this.logger.log(LogLevel.Warning, "No client method with the name '" + invocationMessage.target + "' found.");
    }
  };

  HubConnection.prototype.connectionClosed = function (error) {
    var _this = this;

    var callbacks = this.callbacks;
    this.callbacks = {};
    this.connectionState = HubConnectionState.Disconnected; // if handshake is in progress start will be waiting for the handshake promise, so we complete it
    // if it has already completed this should just noop

    if (this.handshakeRejecter) {
      this.handshakeRejecter(error);
    }

    Object.keys(callbacks).forEach(function (key) {
      var callback = callbacks[key];
      callback(null, error ? error : new Error("Invocation canceled due to connection being closed."));
    });
    this.cleanupTimeout();
    this.cleanupPingTimer();
    this.closedCallbacks.forEach(function (c) {
      return c.apply(_this, [error]);
    });
  };

  HubConnection.prototype.cleanupPingTimer = function () {
    if (this.pingServerHandle) {
      clearTimeout(this.pingServerHandle);
    }
  };

  HubConnection.prototype.cleanupTimeout = function () {
    if (this.timeoutHandle) {
      clearTimeout(this.timeoutHandle);
    }
  };

  HubConnection.prototype.createInvocation = function (methodName, args, nonblocking) {
    if (nonblocking) {
      return {
        arguments: args,
        target: methodName,
        type: MessageType.Invocation
      };
    } else {
      var id = this.id;
      this.id++;
      return {
        arguments: args,
        invocationId: id.toString(),
        target: methodName,
        type: MessageType.Invocation
      };
    }
  };

  HubConnection.prototype.createStreamInvocation = function (methodName, args) {
    var id = this.id;
    this.id++;
    return {
      arguments: args,
      invocationId: id.toString(),
      target: methodName,
      type: MessageType.StreamInvocation
    };
  };

  HubConnection.prototype.createCancelInvocation = function (id) {
    return {
      invocationId: id,
      type: MessageType.CancelInvocation
    };
  };

  return HubConnection;
}();

export { HubConnection };